import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { KTSVG } from "../../../../_metronic/helpers";
import { useNearbyExperiences } from "../../../hooks/members/useNearbyExperiences";
import { getCategories } from "../../../services/teamExperienceService";
import { ExperienceSummary } from "../../teamExperiences/models/ExperienceSummaryModel";
import { ExperienceCard } from "./ExperienceCard";
import { MemberExperienceCategories } from "./MemberExperienceCategories";
import { PromoBanners } from "./PromoBanners";

// Mismos umbrales que MemberSearchList para mantener consistencia visual:
// mobile/tablet (<992) solo swipe tactil, desktop (>=992) muestra flechas.
const ARROWS_MIN_WIDTH_PX = 992
// PR-I-76: card de experience ahora mide 400px (base del mockup) + gap 25px
// definido en `.container-experience-search-wrapper-cards--carousel`, entonces
// cada click de flecha avanza exactamente una card. Ajustar aca si cambia el
// ancho de card en `_nb-experience-preview.scss` o el gap en `_nb-member-search.scss`.
const SCROLL_STEP_PX = 425

interface Props {
    items?: ExperienceSummary[];
    popularItems?: ExperienceSummary[];
    eventsScroll?: boolean;
    loading?: boolean;
    onChangeCategory: (category:number) => void;
    search?: string;
    selectedCategoryId?: string | null;
    onSelectedCategoryChange?: (id: string | null) => void;
}

// Hook privado: encapsula la logica de flechas/edges para no duplicarla
// entre los dos carruseles (Invitations + Most Popular). Cada carrusel usa
// su propio ref y suscripcion a resize.
const useCarouselArrows = (dep: unknown) => {
    const ref = useRef<HTMLDivElement>(null)
    const [arrowsEnabled, setArrowsEnabled] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= ARROWS_MIN_WIDTH_PX : true,
    )
    const [canPrev, setCanPrev] = useState(false)
    const [canNext, setCanNext] = useState(false)

    const updateEdges = useCallback(() => {
        const el = ref.current
        if (!el) return
        const {scrollLeft, scrollWidth, clientWidth} = el
        // Simetria en ambos extremos: el padding horizontal (4px) + gap (25px)
        // hace que scrollLeft nunca llegue exacto a 0 ni scrollWidth-clientWidth,
        // aun cuando visualmente ya no hay mas cards para scrollear. 40px de
        // tolerancia cubre el peor caso y evita ver una flecha "apuntando a
        // la nada" en cualquiera de los dos lados.
        setCanPrev(scrollLeft > 40)
        setCanNext(scrollLeft + clientWidth < scrollWidth - 40)
    }, [])

    useEffect(() => {
        const onResize = () => {
            setArrowsEnabled(window.innerWidth >= ARROWS_MIN_WIDTH_PX)
            updateEdges()
        }
        onResize()
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [updateEdges])

    useEffect(() => {
        updateEdges()
    }, [dep, updateEdges])

    const scrollBy = (offset: number) => {
        const el = ref.current
        if (!el) return
        el.scrollBy({left: offset, behavior: 'smooth'})
    }

    return {ref, arrowsEnabled, canPrev, canNext, updateEdges, scrollBy}
}

const CarouselArrows: React.FC<{
    label: string
    canPrev: boolean
    canNext: boolean
    arrowsEnabled: boolean
    onPrev: () => void
    onNext: () => void
}> = ({label, canPrev, canNext, arrowsEnabled, onPrev, onNext}) => {
    if (!arrowsEnabled) return null
    return (
        <>
            {canPrev && (
                <button
                    type='button'
                    aria-label={`Previous ${label}`}
                    className='members-page__experiences-arrow members-page__experiences-arrow--prev'
                    onClick={onPrev}
                >
                    <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M8.5 1L1.5 8L8.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                </button>
            )}
            {canNext && (
                <button
                    type='button'
                    aria-label={`Next ${label}`}
                    className='members-page__experiences-arrow members-page__experiences-arrow--next'
                    onClick={onNext}
                >
                    <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M1.5 1L8.5 8L1.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                </button>
            )}
        </>
    )
}

export const MemberExperiences: React.FC<Props> = ({ items, popularItems, onChangeCategory, search, selectedCategoryId, onSelectedCategoryChange }: Props) => {
    const [categories, setCategories] = useState([]);
    const { list: nearby } = useNearbyExperiences(search);

    // Un set de flechas por carrusel — cada fila tiene su propio scroll,
    // por eso no se pueden compartir ni el ref ni los estados de edges.
    const invitations = useCarouselArrows(items)
    const popular = useCarouselArrows(popularItems)

    useEffect(() => {
        onInitComponent()
    }, [])

    const onInitComponent = async () => {
        let categories_ = await getCategories()
        setCategories(categories_?.results || [])
    }

    return (<>
        <div className='bg-experiences-section-black'>
            <div className="container-experience-search">
                <div className='members-page mt-experiences-section-black'>
                    <div className='members-page__header'>
                        {/* PR-I-54: title deep-links to the full experiences search (no filters). */}
                        <Link to='/experiences-search' className='members-page__title-link'>
                            <h1 className='members-page__title text-white'>
                                Invitations&nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                            </h1>
                        </Link>
                    </div>
                </div>
                <MemberExperienceCategories data={categories} loading={false} onChangeCategory={onChangeCategory} selectedId={selectedCategoryId} onSelectedIdChange={onSelectedCategoryChange} />

                {/* Fila 1: Invitations — filtrable por categoria, carrusel con flechas */}
                <div className='members-page__experiences-carousel'>
                    <CarouselArrows
                        label='invitations'
                        arrowsEnabled={invitations.arrowsEnabled}
                        canPrev={invitations.canPrev}
                        canNext={invitations.canNext}
                        onPrev={() => invitations.scrollBy(-SCROLL_STEP_PX)}
                        onNext={() => invitations.scrollBy(SCROLL_STEP_PX)}
                    />
                    <div
                        className='container-experience-search-wrapper-cards container-experience-search-wrapper-cards--carousel'
                        ref={invitations.ref}
                        onScroll={invitations.updateEdges}
                    >
                        {(items ?? []).map((item, idx) => (
                            <ExperienceCard
                                experience={item}
                                key={idx}
                            />
                        ))}
                    </div>
                </div>

                {/* Fila 2: Most Popular Experiences — subtitulo mas chico, sin
                    filtro por categoria, mismo carrusel visual */}
                {!!popularItems && popularItems.length > 0 && (
                    <>
                        <div className='members-page mt-experiences-section-black-subtitle'>
                            <div className='members-page__header'>
                                <h2 className='members-page__subtitle'>
                                    Most Popular Experiences
                                </h2>
                            </div>
                        </div>
                        <div className='members-page__experiences-carousel'>
                            <CarouselArrows
                                label='popular experiences'
                                arrowsEnabled={popular.arrowsEnabled}
                                canPrev={popular.canPrev}
                                canNext={popular.canNext}
                                onPrev={() => popular.scrollBy(-SCROLL_STEP_PX)}
                                onNext={() => popular.scrollBy(SCROLL_STEP_PX)}
                            />
                            <div
                                className='container-experience-search-wrapper-cards container-experience-search-wrapper-cards--carousel'
                                ref={popular.ref}
                                onScroll={popular.updateEdges}
                            >
                                {popularItems.map((item, idx) => (
                                    <ExperienceCard
                                        experience={item}
                                        key={idx}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}

                <div className='promo-banners-inside-invitations'>
                    <PromoBanners variant='borderless' />
                </div>
                {!!search && !!nearby && (
                    <>
                        <hr className="mt-12 mb-12" style={{ color: 'white' }} />
                        <div className='members-page mt-experiences-section-black mt-0'>
                            <div className='members-page__header'>
                                <h1 className='members-page__title text-white'>
                                    Nearby Experiences
                                </h1>
                            </div>
                        </div>
                        <div className='container-experience-search-wrapper-cards'>
                            {nearby.map((exp, idx) => (
                                <ExperienceCard
                                    experience={exp}
                                    key={idx}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    </>);
};
